import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, MapPin, BadgeCheck, Sparkles, ArrowLeft, ClipboardList } from "lucide-react";
import { ConsumerHeader } from "@/pages/ConsumerHome";
import { api, apiError, peso } from "@/lib/api";
import { PropertyCover } from "@/components/AdaptiveMediaFrame";
import { Button } from "@/components/ui/button";

function specLine(p) {
  const parts = [];
  if (p.bedrooms > 0) parts.push(`${p.bedrooms} Bed${p.bedrooms > 1 ? "s" : ""}`);
  if (p.bathrooms > 0) parts.push(`${p.bathrooms} Bath${p.bathrooms > 1 ? "s" : ""}`);
  if (p.parking_slots > 0) parts.push(`${p.parking_slots} Parking`);
  if (p.floor_area_sqm > 0) parts.push(`${p.floor_area_sqm} sqm`);
  return parts.join(" · ");
}

// Friendly, restrained bands derived deterministically from the engine's numeric score.
function bandLabel(score) {
  if (score >= 90) return "Excellent Match";
  if (score >= 80) return "Strong Match";
  if (score >= 70) return "Good Match";
  return "Possible Match";
}

function MatchCard({ p }) {
  const rent = p.listing_type === "For Rent";
  const score = p.match?.score ?? 0;
  const label = bandLabel(score);
  const lowConf = p.match?.confidence_label && /low|limited|reduced/i.test(p.match.confidence_label);
  const specs = specLine(p);
  return (
    <Link to={`/listing/${p.id}`} data-testid={`match-card-${p.id}`} className="group self-start block bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#0F2C59] hover:shadow-lg transition-all">
      <PropertyCover listing={p} aspect="4/3" hoverZoom />
      <div className="p-5">
        {/* Badges live below the artwork — never overlaid on the creative. */}
        <div className="flex items-center justify-between gap-2 mb-2.5">
          <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${rent ? "bg-[#0F2C59] text-white" : "bg-[#C79A3B] text-white"}`}>{rent ? "For Rent" : "For Sale"}</span>
          <span data-testid={`match-band-${p.id}`} className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-600 text-white inline-flex items-center gap-1 shrink-0"><Sparkles className="h-3 w-3" /> {label} · {score}%</span>
        </div>
        <div className="font-display text-xl font-bold text-slate-900">{peso(p.price)}</div>
        <h3 className="text-sm font-semibold text-slate-800 mt-1 line-clamp-1">{p.title}</h3>
        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.city}{p.project ? ` · ${p.project}` : ""}</p>
        {specs && <p className="text-xs text-slate-600 mt-2">{specs}</p>}
        {p.owner && (
          <div className="flex items-center gap-2 mt-3">
            <img src={p.owner.avatar} alt="" className="h-6 w-6 rounded-full bg-slate-100" />
            <span className="text-xs text-slate-600 truncate">{p.owner.name}</span>
            {p.owner.verification_status === "VERIFIED" && <BadgeCheck className="h-3.5 w-3.5 text-[#0F2C59]" title="Verified professional" />}
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Why it matches</p>
          <p data-testid={`match-why-${p.id}`} className="text-xs text-slate-600 mt-1 leading-relaxed">{p.explanation}</p>
          {lowConf && <p className="text-[11px] text-amber-600 mt-1">Confidence is reduced — some listing details are unavailable.</p>}
        </div>
      </div>
    </Link>
  );
}

export default function ConsumerMatches() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/me/requirements/${id}/matches`); setData(data); }
      catch (e) { setErr(apiError(e)); }
    })();
  }, [id]);

  const req = data?.requirement;
  const results = data?.results || [];
  const rent = req?.listing_type === "For Rent";
  const loc = (req?.cities || [])[0] || "any location";

  return (
    <div className="min-h-screen bg-[#F7F8FA]" data-testid="consumer-matches">
      <ConsumerHeader />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => nav("/")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" /> Back to home</button>

        {err && <div data-testid="matches-error" className="bg-red-50 text-red-600 text-sm rounded-xl p-4">{err}</div>}

        {!data && !err && <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>}

        {data && (
          <>
            <div className="flex items-center gap-2 text-accent">
              <Sparkles className="h-5 w-5" />
              <span className="text-xs font-bold uppercase tracking-widest">Smart Matches · engine {data.engine_version}</span>
            </div>
            <h1 className="font-display text-3xl font-bold text-slate-900 mt-2">Smart Matches</h1>
            <p className="text-slate-500 mt-1">Properties matching your {rent ? "Rent" : "Buy"} requirement in {loc}.</p>

            <div className="bg-white border border-slate-200 rounded-xl p-4 mt-5 flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="matches-req-summary">
              <span><span className="text-slate-400">Type:</span> <b>{rent ? "Rent" : "Buy"}</b></span>
              <span><span className="text-slate-400">Property:</span> <b>{req.property_type === "Any" ? "Any / Flexible" : req.property_type}</b></span>
              <span><span className="text-slate-400">Budget:</span> <b>{(req.budget_min || req.budget_max) ? `${peso(req.budget_min || 0)} – ${peso(req.budget_max || 0)}` : "Any"}</b></span>
              {(req.cities || []).length > 0 && <span><span className="text-slate-400">Location:</span> <b>{(req.cities || []).join(", ")}</b></span>}
            </div>

            {results.length === 0 ? (
              <div data-testid="matches-empty" className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center mt-6">
                <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3"><ClipboardList className="h-6 w-6 text-slate-400" /></div>
                <p className="text-slate-700 font-semibold">No Smart Matches yet</p>
                <p className="text-sm text-slate-500 mt-1 mb-5">We couldn't find an active public property that fits this requirement yet.</p>
                <div className="flex gap-3 justify-center">
                  <Button data-testid="matches-edit-req" onClick={() => nav(`/requirements/${id}/edit`)} className="h-10 px-5 rounded-xl bg-accent text-accent-foreground font-semibold">Edit Requirement</Button>
                  <Button data-testid="matches-browse" variant="outline" onClick={() => nav("/marketplace")} className="h-10 px-5 rounded-xl">Browse Marketplace</Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6" data-testid="matches-grid">
                {results.map((p) => <MatchCard key={p.id} p={p} />)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
